import {
  readConfig,
  getCustomerKey,
  getSessionKey,
  getTouchpoint,
  createProductCardRenderer,
  buildSwatchStyle,
  syncCustomerKey,
  sendClickNotification,
  fetchCartPage,
  escapeHtml,
  escapeAttr,
  safeUrl,
  formatPrice,
  hasVoyadoConsent,
} from 'voyado-elevate';

const _syncOnce = syncCustomerKey();
let _loadedWithoutConsent = false;
const _controllers = [];

const RECOMMENDATION_LABELS = {
  ALTERNATIVES: 'Ähnliche Produkte',
  UPSELL: 'Das könnte Ihnen auch gefallen',
  STYLE_WITH: 'Dazu passt',
  PERSONAL: 'Persönliche Empfehlungen',
  TOP_PRODUCTS: 'Top-Produkte',
  NEWEST_PRODUCTS: 'Neu eingetroffen',
  MORE_FROM_SERIES: 'Mehr aus der Serie',
  RECENTLY_VIEWED: 'Zuletzt angesehen',
  FAVORITES: 'Favoriten',
  CART: 'Passend zu Ihrem Warenkorb',
  ADD_TO_CART_RECS: 'Weitere Empfehlungen',
};

function getCartPageMediaStyle(imageAspectRatio) {
  if (imageAspectRatio === 'square') {
    return 'aspect-ratio: 1 / 1;';
  }

  if (imageAspectRatio === 'portrait') {
    return 'aspect-ratio: 3 / 4;';
  }

  if (imageAspectRatio === 'landscape') {
    return 'aspect-ratio: 4 / 3;';
  }

  return '';
}

function getCartPageImageHeight(imageAspectRatio) {
  if (imageAspectRatio === 'portrait') {
    return 533;
  }

  if (imageAspectRatio === 'landscape') {
    return 300;
  }

  return 400;
}

function parseCartItems(value) {
  if (!value) return [];

  try {
    const parsed = JSON.parse(value);
    if (!Array.isArray(parsed)) return [];

    return parsed
      .map((item) => {
        const itemKey = String(item?.itemKey ?? item?.variantKey ?? item?.articleNumber ?? '').trim();
        const quantity = parseInt(String(item?.quantity ?? ''), 10);

        if (!itemKey || !Number.isFinite(quantity) || quantity <= 0) {
          return null;
        }

        return {
          itemKey,
          quantity,
        };
      })
      .filter(Boolean);
  } catch {
    return [];
  }
}

function readSectionConfig(element) {
  const data = element.dataset;

  return {
    sectionId: data.sectionId,
    cartItems: parseCartItems(data.cartItems || '[]'),
    locale: data.voyadoLocale || '',
    algorithmIds: [data.algorithm1, data.algorithm2, data.algorithm3].filter(Boolean),
    limit: parseInt(data.limit || '8', 10) || 8,
    columnsDesktop: parseInt(data.columnsDesktop || '4', 10) || 4,
    columnsMobile: parseInt(data.columnsMobile || '2', 10) || 2,
    showImage: data.showImage !== 'false',
    imageAspectRatio: data.imageAspectRatio || 'natural',
    enableImageSlider: data.enableImageSlider === 'true',
    showPrice: data.showPrice !== 'false',
    showSwatch: true,
    layout: data.layout === 'slider' ? 'slider' : 'grid',
    headingLevel: ['h2', 'h3', 'h4'].includes(data.headingLevel) ? data.headingLevel : 'h2',
    sectionHeading: data.sectionHeading || '',
    sectionHeadingLevel: ['h2', 'h3', 'h4'].includes(data.sectionHeadingLevel) ? data.sectionHeadingLevel : 'h2',
    priceId: data.priceId || '',
    stores: data.stores || '',
    presentCustom: data.presentCustom || '',
  };
}

class VoyadoCartPage {
  constructor(root) {
    this._root = root;
    this._sectionConfig = readSectionConfig(root);
    this._baseConfig = readConfig();
    Object.assign(this, createProductCardRenderer(() => this._sectionConfig, {
      buildSwatchStyleFn: buildSwatchStyle,
      productInfoVariant: 'product',
      getMediaStyle: getCartPageMediaStyle,
      getImageHeight: getCartPageImageHeight,
    }));
    this._loading = document.getElementById(`voyado-rec-loading-${this._sectionConfig.sectionId}`);
    this._lists = document.getElementById(`voyado-rec-lists-${this._sectionConfig.sectionId}`);
    this._isFetching = false;

    this._bindProductCardContainer(this._lists);
  }

  async load() {
    await _syncOnce;
    if (!this._lists) return;
    if (this._isFetching) return;
    if (this._sectionConfig.cartItems.length === 0) {
      this._hideSection();
      return;
    }

    const urlParams = {
      customerKey: getCustomerKey(),
      sessionKey: getSessionKey(),
      market: this._baseConfig.market,
      locale: this._sectionConfig.locale || this._baseConfig.locale,
      touchpoint: getTouchpoint(),
    };

    if (this._sectionConfig.priceId) {
      urlParams.priceId = this._sectionConfig.priceId;
    }

    if (this._sectionConfig.stores) {
      urlParams.stores = this._sectionConfig.stores;
    }

    if (this._sectionConfig.presentCustom) {
      urlParams.presentCustom = this._sectionConfig.presentCustom;
    }

    if (new URLSearchParams(location.search).get('PREVIEW') === 'true') urlParams.viewId = 'preview';

    const algorithmIds = this._sectionConfig.algorithmIds.length > 0
      ? this._sectionConfig.algorithmIds
      : ['CART'];

    const visualization = this._sectionConfig.layout === 'slider' ? 'CAROUSEL' : 'GRID';
    const body = {
      cartItems: this._sectionConfig.cartItems,
      recommendationLists: algorithmIds.map((algorithm, index) => ({
        id: `list-${index + 1}`,
        algorithm,
        label: this._getRecommendationLabel(algorithm),
        limit: this._sectionConfig.limit,
        visualization,
      })),
    };

    this._showLoading();
    this._isFetching = true;

    try {
      const data = await fetchCartPage(this._baseConfig.cluster, urlParams, body);

      // Track whether this load ran without consent so we can re-fetch when consent arrives
      if (!hasVoyadoConsent()) {
        _loadedWithoutConsent = true;
      }

      const lists = Array.isArray(data?.recommendationLists) ? data.recommendationLists : [];
      const rendered = this._renderRecommendations(lists);

      if (!rendered) {
        this._hideSection();
        return;
      }

      this._root.hidden = false;
    } catch {
      this._hideSection();
    } finally {
      this._isFetching = false;
      if (this._loading) {
        this._loading.hidden = true;
      }
    }
  }

  _showLoading() {
    if (this._loading) {
      this._root.hidden = false;
      this._loading.hidden = false;
    }

    if (this._lists) {
      this._lists.hidden = true;
      this._lists.innerHTML = '';
    }
  }

  _hideSection() {
    if (this._lists) {
      this._lists.hidden = true;
      this._lists.innerHTML = '';
    }

    if (this._loading) {
      this._loading.hidden = true;
    }

    this._root.hidden = true;
  }

  _renderRecommendations(lists) {
    if (!this._lists) return false;

    const recommendationLists = Array.isArray(lists) ? lists : [];

    if (recommendationLists.length === 0) {
      this._lists.innerHTML = '';
      this._lists.hidden = true;
      return false;
    }

    const sectionsHtml = recommendationLists
      .map((list, listIndex) => {
        if (list?.visible === false) return '';

        const productGroups = this._normalizeProductGroups(list?.productGroups ?? list?.products ?? []);
        if (productGroups.length === 0) return '';

        const cardsHtml = productGroups
          .map((productGroup) => this._renderProductCard(productGroup))
          .filter(Boolean)
          .join('');

        if (!cardsHtml) return '';

        const listName = String(list?.label ?? list?.id ?? '').trim();
        const headingTag = this._sectionConfig.headingLevel;
        const isSlider = this._sectionConfig.layout === 'slider';

        return `
          <section class="voyado-rec__list">
            ${listName ? `<div class="prose prose--tight"><${headingTag} class="h4">${escapeHtml(listName)}</${headingTag}></div>` : ''}
            ${isSlider ? this._renderSliderInner(cardsHtml, listIndex) : this._renderGridInner(cardsHtml)}
          </section>
        `;
      })
      .filter(Boolean)
      .join('');

    if (!sectionsHtml) {
      this._lists.innerHTML = '';
      this._lists.hidden = true;
      return false;
    }

    const sectionHeading = this._sectionConfig.sectionHeading.trim();
    const sectionHeadingTag = this._sectionConfig.sectionHeadingLevel;

    this._lists.hidden = false;
    this._lists.innerHTML = `
      ${sectionHeading ? `<div class="prose voyado-rec__section-heading"><${sectionHeadingTag}>${escapeHtml(sectionHeading)}</${sectionHeadingTag}></div>` : ''}
      ${sectionsHtml}
    `;

    return true;
  }

  _renderGridInner(cardsHtml) {
    const cols = escapeAttr(String(this._sectionConfig.columnsDesktop));
    const colsMobile = escapeAttr(String(this._sectionConfig.columnsMobile));
    return `<div class="voyado-search__grid" style="--columns-desktop:${cols};--columns-mobile:${colsMobile}">${cardsHtml}</div>`;
  }

  _renderSliderInner(cardsHtml, listIndex) {
    const sliderId = escapeAttr(`voyado-rec-slider-${this._sectionConfig.sectionId}-${listIndex}`);
    return `
      <div class="floating-controls-container floating-controls-container--inside floating-controls-container--on-hover">
        <scroll-carousel id="${sliderId}" group-cells allow-drag class="product-list product-list--carousel scroll-area bleed md:unbleed" style="--product-list-items-per-row:${this._sectionConfig.columnsDesktop}">
          ${cardsHtml}
        </scroll-carousel>
        <button is="carousel-prev-button" type="button" class="prev-next-button prev-next-button--prev circle-button hover:animate-icon-inline" aria-controls="${sliderId}" disabled>
          <span class="sr-only">Zurück</span>
          <svg aria-hidden="true" focusable="false" fill="none" width="16" viewBox="0 0 16 18"><path d="M11 1 3 9l8 8" stroke="currentColor" stroke-linecap="square"/></svg>
        </button>
        <button is="carousel-next-button" type="button" class="prev-next-button prev-next-button--next circle-button hover:animate-icon-inline" aria-controls="${sliderId}">
          <span class="sr-only">Weiter</span>
          <svg aria-hidden="true" focusable="false" fill="none" width="16" viewBox="0 0 16 18"><path d="m5 17 8-8-8-8" stroke="currentColor" stroke-linecap="square"/></svg>
        </button>
      </div>
    `;
  }

  _canRenderSwatches() {
    return this._sectionConfig.showSwatch && this._baseConfig.showSwatches;
  }

  _getRecommendationLabel(algorithm) {
    const key = String(algorithm || '').trim().toUpperCase();
    return RECOMMENDATION_LABELS[key] || 'Empfehlungen';
  }
}

function initializeVoyadoCartPage() {
  const sections = document.querySelectorAll('[data-voyado-cart-page]');

  sections.forEach((section) => {
    if (!(section instanceof HTMLElement)) return;

    try {
      const controller = new VoyadoCartPage(section);
      _controllers.push(controller);
      controller.load();
    } catch {
      section.hidden = true;
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeVoyadoCartPage, { once: true });
} else {
  initializeVoyadoCartPage();
}

// Re-fetch cart page recommendations when the visitor accepts cookies after the initial load
document.addEventListener('visitorConsentCollected', () => {
  if (!_loadedWithoutConsent) return;
  _loadedWithoutConsent = false;
  _controllers.forEach((controller) => {
    controller._isFetching = false;
    controller.load();
  });
}, { once: true });
